import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Plus,
  TrendingUp,
  Ruler,
  Activity,
  Edit,
  Trash2,
  AlertCircle,
  X,
  FileText,
  Image as ImageIcon
} from 'lucide-react';
import moment from 'moment';
import { studentsAPI } from '../../services/api';
import StudentMeasurementModal from './StudentMeasuremetModal';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../HasPermission';

const StudentMeasurementsTab = ({ student, organizationId }) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyTab, setHistoryTab] = useState('body');

  useEffect(() => {
    fetchMeasurements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, student.id]);

  const fetchMeasurements = async () => {
    try {
      setLoading(true);
      const response = await studentsAPI.getMeasurements(organizationId, student.id);
      setMeasurements(response.data.data || []);
    } catch (err) {
      console.error('Error fetching measurements:', err);
      setError('Failed to load measurements');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMeasurement = () => {
    setEditingMeasurement(null);
    setShowAddModal(true);
  };

  const handleEditMeasurement = (measurement) => {
    setEditingMeasurement(measurement);
    setShowAddModal(true);
  };

  const handleSaveMeasurement = async (measurementData) => {
    try {
      setSaving(true);

      if (editingMeasurement) {
        await studentsAPI.updateMeasurement(organizationId, student.id, editingMeasurement.id, measurementData);
      } else {
        await studentsAPI.addMeasurement(organizationId, student.id, measurementData);
      }

      await fetchMeasurements();
      setShowAddModal(false);
      setEditingMeasurement(null);
    } catch (err) {
      console.error('Error saving measurement:', err);
      setError('Failed to save measurement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMeasurement = async (measurementId) => {
    if (!window.confirm(t('studentProfile.measurements.deleteConfirm'))) return;

    try {
      await studentsAPI.deleteMeasurement(organizationId, student.id, measurementId);
      await fetchMeasurements();
    } catch (err) {
      console.error('Error deleting measurement:', err);
      setError('Failed to delete measurement');
    }
  };

  const getLatestMeasurement = () => {
    if (measurements.length === 0) return null;
    return measurements.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  };

  const getMeasurementTrend = (field) => {
    if (measurements.length < 2) return null;

    const sortedMeasurements = measurements
      .filter(m => m[field])
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    if (sortedMeasurements.length < 2) return null;

    const latest = sortedMeasurements[sortedMeasurements.length - 1][field];
    const previous = sortedMeasurements[sortedMeasurements.length - 2][field];

    const change = latest - previous;
    return {
      change: change,
      percentage: ((change / previous) * 100).toFixed(1),
      isIncrease: change > 0
    };
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${colors.bgPrimary}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const latestMeasurement = getLatestMeasurement();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className={`text-lg font-medium ${colors.textPrimary}`}>{t('studentProfile.measurements.title')}</h2>
        {hasPermission('create:measurements') && (
          <button
            onClick={handleAddMeasurement}
            className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span>{t('studentProfile.measurements.addMeasurement')}</span>
          </button>
        )}
      </div>

      {error && (
        <div className={`bg-red-50 border ${colors.inputBorder} rounded-xl p-4 flex items-center`}>
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {measurements.length === 0 ? (
        <div className={`${colors.cardBg} text-center py-12 rounded-xl border ${colors.inputBorder}`}>
          <Activity className={`w-12 h-12 ${colors.textSecondary} mx-auto mb-4`} />
          <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>{t('studentProfile.measurements.noMeasurements')}</h3>
          <p className={`${colors.textSecondary} mb-6`}>{t('studentProfile.measurements.noMeasurementsDesc')}</p>
          {hasPermission('create:measurements') && (
            <button
              onClick={handleAddMeasurement}
              className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]"
            >
              {t('studentProfile.measurements.addFirst')}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Latest Measurements Summary */}
          {latestMeasurement && (
            <div className={`${colors.cardBg} rounded-xl border ${colors.inputBorder} p-6`}>
              <h3 className={`text-lg font-medium ${colors.textPrimary} mb-4`}>{t('studentProfile.measurements.latest')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[
                  { key: 'weight', label: t('studentProfile.measurements.labels.weight'), unit: 'kg', icon: Activity },
                  { key: 'height', label: t('studentProfile.measurements.labels.height'), unit: 'cm', icon: Ruler },
                  { key: 'chest', label: t('studentProfile.measurements.labels.chest'), unit: 'cm', icon: Activity },
                  { key: 'waist', label: t('studentProfile.measurements.labels.waist'), unit: 'cm', icon: Activity },
                  { key: 'hip', label: t('studentProfile.measurements.labels.hip'), unit: 'cm', icon: Activity },
                  { key: 'bmi', label: t('studentProfile.measurements.labels.bmi'), unit: '', icon: TrendingUp }
                ].map(({ key, label, unit, icon: Icon }) => {
                  const trend = getMeasurementTrend(key);
                  return (
                    <div key={key} className={`${colors.bgTertiary} border ${colors.border} rounded-lg p-3 transition-colors ${colors.bgHover}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${colors.textSecondary}`}>{label}</span>
                        <Icon className="w-3 h-3 text-zinc-600" />
                      </div>
                      <div className={`text-base font-bold ${colors.textPrimary}`}>
                        {latestMeasurement[key] ? `${latestMeasurement[key]}${unit}` : '-'}
                      </div>
                      {trend && (
                        <div className={`flex items-center text-[10px] mt-1 font-bold ${trend.isIncrease ? 'text-red-500/80' : 'text-emerald-500/80'
                          }`}>
                          <TrendingUp className={`w-3 h-3 mr-1 ${!trend.isIncrease ? 'rotate-180' : ''}`} />
                          {Math.abs(trend.change).toFixed(1)}{unit} ({trend.percentage}%)
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Transformation Photos */}
              {(latestMeasurement.frontImageUrl || latestMeasurement.backImageUrl || latestMeasurement.leftImageUrl || latestMeasurement.rightImageUrl) && (
                <div className={`mt-8 pt-6 border-t ${colors.border}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`text-xs font-bold ${colors.textTertiary} uppercase tracking-widest`}>Değişim Fotoğrafları</h4>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { url: latestMeasurement.frontImageUrl, label: 'ÖN' },
                      { url: latestMeasurement.backImageUrl, label: 'ARKA' },
                      { url: latestMeasurement.leftImageUrl, label: 'SOL' },
                      { url: latestMeasurement.rightImageUrl, label: 'SAĞ' }
                    ].map((img, idx) => img.url && (
                      <div key={idx} className={`relative group overflow-hidden rounded border ${colors.border} ${colors.bgTertiary} h-40`}>
                        <img src={img.url} alt={img.label} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2">
                          <span className="text-[9px] font-bold text-white uppercase tracking-widest">{img.label}</span>
                        </div>
                        <a href={img.url} target="_blank" rel="noopener noreferrer" className={`absolute top-2 right-2 p-1.5 ${isDark ? 'bg-zinc-950/80' : 'bg-white/80'} rounded border border-white/5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                          <ImageIcon className="w-3 h-3 text-white" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional measurements if available */}
              {(latestMeasurement.caliperBodyFatPercentage || latestMeasurement.tanitaBodyFatPercentage || latestMeasurement.tanitaMusclePercentage) && (
                <div className={`mt-6 pt-4 border-t ${colors.tableBorder}`}>
                  <h4 className={`text-md font-medium ${colors.textPrimary} mb-3`}>{t('studentProfile.measurements.bodyComposition')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {latestMeasurement.caliperBodyFatPercentage && (
                      <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3">
                        <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1">{t('studentProfile.measurements.labels.caliperBodyFat')}</div>
                        <div className="text-base font-bold text-emerald-400">
                          {latestMeasurement.caliperBodyFatPercentage}%
                        </div>
                      </div>
                    )}

                    {latestMeasurement.tanitaBodyFatPercentage && (
                      <div className="bg-sky-500/5 border border-sky-500/10 rounded-lg p-3">
                        <div className="text-[10px] font-bold text-sky-500 uppercase tracking-widest mb-1">{t('studentProfile.measurements.labels.tanitaBodyFat')}</div>
                        <div className="text-base font-bold text-sky-400">
                          {latestMeasurement.tanitaBodyFatPercentage}%
                        </div>
                      </div>
                    )}

                    {latestMeasurement.tanitaMusclePercentage && (
                      <div className="bg-violet-500/5 border border-violet-500/10 rounded-lg p-3">
                        <div className="text-[10px] font-bold text-violet-500 uppercase tracking-widest mb-1">{t('studentProfile.measurements.labels.muscleMass')}</div>
                        <div className="text-base font-bold text-violet-400">
                          {latestMeasurement.tanitaMusclePercentage}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className={`mt-3 text-sm ${colors.textSecondary}`}>
                {t('studentProfile.measurements.recordedOn', { date: moment(latestMeasurement.date).format('MMMM DD, YYYY') })}
              </div>
            </div>
          )}

          {/* Measurements History */}
          <div className={`${colors.cardBg} rounded-xl border ${colors.inputBorder}`}>
            <div className={`px-6 py-4 border-b ${colors.tableBorder} flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
              <h3 className={`text-lg font-medium ${colors.textPrimary}`}>{t('studentProfile.measurements.history')}</h3>

              {/* History Tabs */}
              <div className={`flex space-x-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'} p-1 rounded-lg`}>
                <button
                  onClick={() => setHistoryTab('body')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${historyTab === 'body'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  {t('studentProfile.measurements.modal.tabs.body')}
                </button>
                <button
                  onClick={() => setHistoryTab('caliper')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${historyTab === 'caliper'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  {t('studentProfile.measurements.modal.tabs.caliper')}
                </button>
                <button
                  onClick={() => setHistoryTab('tanita')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${historyTab === 'tanita'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  {t('studentProfile.measurements.modal.tabs.tanita')}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${colors.inputBg}`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                      {t('studentProfile.measurements.table.date')}
                    </th>

                    {historyTab === 'body' && (
                      <>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.table.weight')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.table.height')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.table.chest')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.table.waist')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.table.hip')}
                        </th>
                         <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.table.bmi')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          GÖRSEL VERİLER
                        </th>
                      </>
                    )}

                    {historyTab === 'caliper' && (
                      <>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.caliper.triceps')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.caliper.suprailiac')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.caliper.thigh')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.caliper.bodyFatAuto')}
                        </th>
                      </>
                    )}

                    {historyTab === 'tanita' && (
                      <>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.tanita.bodyFat')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.tanita.bodyFatKg')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.tanita.musclePct')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.tanita.muscleKg')}
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                          {t('studentProfile.measurements.modal.tanita.uploads.title')}
                        </th>
                      </>
                    )}

                    <th className={`px-6 py-3 text-left text-xs font-medium ${colors.textSecondary} uppercase tracking-wider`}>
                      {t('studentProfile.measurements.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className={`${colors.cardBg} divide-y ${colors.tableBorder}`}>
                  {measurements
                    .filter(m => {
                      if (historyTab === 'body') {
                        // Check if any body measurement exists
                        return m.weight || m.height || m.chest || m.waist || m.hip || m.leftArm || m.rightArm || m.leftLeg || m.rightLeg || m.shoulder || m.neck || m.bellyButton || m.thighs || m.frontImageUrl || m.backImageUrl || m.leftImageUrl || m.rightImageUrl;
                      }
                      if (historyTab === 'caliper') {
                        return m.caliperTriceps || m.caliperSuprailiac || m.caliperThigh || m.caliperBodyFatPercentage;
                      }
                      if (historyTab === 'tanita') {
                        return m.tanitaBodyFatPercentage || m.tanitaBodyFatKg || m.tanitaMusclePercentage || m.tanitaMuscleKg || m.tanitaPhotoUrl || m.tanitaPdfUrl;
                      }
                      return true;
                    })
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((measurement) => (
                      <tr key={measurement.id} className={`${colors.bgHover}`}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                          {moment(measurement.date).format('MMM DD, YYYY')}
                        </td>

                        {historyTab === 'body' && (
                          <>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.weight || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.height || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.chest || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.waist || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.hip || '-'}
                            </td>
                             <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.bmi || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              <div className="flex items-center space-x-1.5">
                                {(measurement.frontImageUrl || measurement.backImageUrl || measurement.leftImageUrl || measurement.rightImageUrl) ? (
                                  <div className="flex items-center space-x-1">
                                    {[
                                      { url: measurement.frontImageUrl, label: 'Ön' },
                                      { url: measurement.backImageUrl, label: 'Arka' },
                                      { url: measurement.leftImageUrl, label: 'Sol' },
                                      { url: measurement.rightImageUrl, label: 'Sağ' }
                                    ].map((img, i) => img.url && (
                                      <a key={i} href={img.url} target="_blank" rel="noopener noreferrer" className={`w-6 h-6 rounded ${colors.bgTertiary} border ${colors.border} flex items-center justify-center ${colors.bgHover} transition-colors`} title={img.label}>
                                        <ImageIcon className={`w-3 h-3 ${colors.textTertiary}`} />
                                      </a>
                                    ))}
                                  </div>
                                ) : '-'}
                              </div>
                            </td>
                          </>
                        )}

                        {historyTab === 'caliper' && (
                          <>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.caliperTriceps || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.caliperSuprailiac || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.caliperThigh || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${colors.textPrimary}`}>
                              {measurement.caliperBodyFatPercentage ? `${measurement.caliperBodyFatPercentage}%` : '-'}
                            </td>
                          </>
                        )}

                        {historyTab === 'tanita' && (
                          <>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${colors.textPrimary}`}>
                              {measurement.tanitaBodyFatPercentage ? `${measurement.tanitaBodyFatPercentage}%` : '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.tanitaBodyFatKg || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.tanitaMusclePercentage ? `${measurement.tanitaMusclePercentage}%` : '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              {measurement.tanitaMuscleKg || '-'}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}>
                              <div className="flex items-center space-x-2">
                                {measurement.tanitaPhotoUrl && (
                                  <a
                                    href={measurement.tanitaPhotoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                    title={t('studentProfile.measurements.modal.tanita.uploads.photo')}
                                  >
                                    <ImageIcon className="w-4 h-4 text-blue-600" />
                                  </a>
                                )}
                                {measurement.tanitaPdfUrl && (
                                  <a
                                    href={measurement.tanitaPdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                                    title={t('studentProfile.measurements.modal.tanita.uploads.pdf')}
                                  >
                                    <FileText className="w-4 h-4 text-red-500" />
                                  </a>
                                )}
                                {!measurement.tanitaPhotoUrl && !measurement.tanitaPdfUrl && '-'}
                              </div>
                            </td>
                          </>
                        )}

                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textSecondary}`}>
                          <div className="flex space-x-2">
                            {hasPermission('update:measurements') && (
                              <button
                                onClick={() => handleEditMeasurement(measurement)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {hasPermission('delete:measurements') && (
                              <button
                                onClick={() => handleDeleteMeasurement(measurement.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      <StudentMeasurementModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveMeasurement}
        editingMeasurement={editingMeasurement}
        saving={saving}
        student={student}
      />
    </div>
  );
};

export default StudentMeasurementsTab;